import React, { useState, useEffect } from "react";
import "./AtreyaScholarshipBanner.css";

// Import both mobile and web images
import scholarshipBannerMobile from "../../../accerts/svg/ScholarshipBanner/Atreyascholarship-banner-mobile.svg";
import scholarshipBannerWeb from "../../../accerts/svg/ScholarshipBanner/Atreyascholarship-banner-web.svg";

const SCHOLARSHIP_RULES_URL = [
  {
    12: [
      {
        iit_jee: {
          "0-20": "20",
          "20-30": "25",
          "30-40": "30",
          "40-50": "35",
          "50-60": "40",
          "60-100": "45",
        },
      },
      {
        neet: {
          "0-20": "20",
          "20-30": "25",
          "30-40": "30",
          "40-50": "35",
          "50-60": "40",
          "60-100": "45",
        },
      },
    ],
  },
  {
    13: [
      {
        iit_jee: {
          "0-19": "20",
          "20-29": "25",
          "30-39": "30",
          "40-49": "35",
          "50-59": "40",
          "60-100": "45",
        },
      },
      {
        neet: {
          "0-19": "20",
          "20-29": "25",
          "30-39": "30",
          "40-49": "35",
          "50-59": "40",
          "60-100": "45",
        },
      },
    ],
  },
];

const AtreyaScholarshipBanner = ({
  grade,
  examType,
  totalMarks,
  marksObtained,
}) => {
  const [scholarshipAmount, setScholarshipAmount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rulesData, setRulesData] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile view
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 700;
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Load rules data
  useEffect(() => {
    setRulesData(SCHOLARSHIP_RULES_URL);
    setLoading(false);
  }, []);

  // Calculate scholarship
  useEffect(() => {
    if (!rulesData) return;

    const calculateScholarship = () => {
      if (
        !grade ||
        !examType ||
        !totalMarks ||
        marksObtained === undefined ||
        marksObtained === null
      ) {
        setScholarshipAmount(null);
        return;
      }

      try {
        const safeMarksObtained = Math.max(0, parseFloat(marksObtained));
        const safeTotalMarks = Math.max(1, parseFloat(totalMarks));

        // FIXED: Calculate percentage correctly
        const percentage = Math.round(
          (safeMarksObtained / safeTotalMarks) * 100,
        );

        console.log(
          `📊 Percentage: ${percentage}% (${safeMarksObtained}/${safeTotalMarks})`,
        );

        const amount = findMatchingScholarship(grade, examType, percentage);
        setScholarshipAmount(amount);
      } catch (err) {
        console.error("Error calculating scholarship:", err);
        setScholarshipAmount(null);
      }
    };

    const normalizeExamType = (examType) => {
      if (!examType) return "";

      const lowerExam = examType.toLowerCase().trim();

      if (lowerExam.includes("jee") || lowerExam.includes("iit")) {
        return "iit_jee";
      }

      if (lowerExam.includes("neet")) {
        return "neet";
      }

      if (
        lowerExam.includes("foundation") ||
        lowerExam.includes("foundational")
      ) {
        return "foundation";
      }

      return lowerExam.replace(/\s+/g, "_");
    };

    // FIXED: Better range matching with inclusive boundaries
    const isPercentageInRange = (percentage, rangeStr) => {
      if (!rangeStr || typeof rangeStr !== "string") return false;

      rangeStr = rangeStr.trim();
      const parts = rangeStr.split("-");
      if (parts.length !== 2) return false;

      let min = parseFloat(parts[0]);
      let max = parseFloat(parts[1]);

      if (isNaN(min) || isNaN(max)) return false;

      // Make ranges inclusive on both ends
      // For 0-20: includes 0, 1, 2, ..., 20
      // For 20-30: includes 20, 21, 22, ..., 30
      // For 60-100: includes 60, 61, ..., 100
      if (percentage >= min && percentage <= max) {
        return true;
      }

      // Handle edge case: if percentage is exactly at the boundary
      if (percentage === min || percentage === max) {
        return true;
      }

      return false;
    };

    const findMatchingScholarship = (grade, examType, percentage) => {
      const gradeStr = grade.toString();

      // Find the grade object in rulesData
      const gradeObject = rulesData.find((item) => item[gradeStr]);

      if (!gradeObject) {
        console.log(`❌ No grade object found for grade: ${gradeStr}`);
        return null;
      }

      const gradeRules = gradeObject[gradeStr];
      const normalizedExamType = normalizeExamType(examType);

      if (!normalizedExamType) {
        console.log(`❌ Invalid exam type: ${examType}`);
        return null;
      }

      // Find the exam object
      const examObject = gradeRules.find((item) => item[normalizedExamType]);

      if (!examObject) {
        console.log(
          `❌ No exam object found for: ${normalizedExamType} in grade ${gradeStr}`,
        );
        return null;
      }

      const examRules = examObject[normalizedExamType];

      // Dynamically find the matching percentage range
      let matchingAmount = null;
      let matchedRange = null;

      // Sort ranges by their minimum value to ensure proper matching
      const sortedRanges = Object.keys(examRules).sort((a, b) => {
        const minA = parseFloat(a.split("-")[0]);
        const minB = parseFloat(b.split("-")[0]);
        return minA - minB;
      });

      for (const range of sortedRanges) {
        if (isPercentageInRange(percentage, range)) {
          matchingAmount = parseFloat(examRules[range]);
          matchedRange = range;
          break;
        }
      }

      if (matchingAmount !== null) {
        console.log(
          `✅ Scholarship matched: ${matchingAmount}% for ${percentage}% (range: ${matchedRange}) for Grade ${gradeStr}, Exam: ${normalizedExamType}`,
        );
      } else {
        console.log(
          `❌ No scholarship range matched for ${percentage}% in Grade ${gradeStr}, Exam: ${normalizedExamType}`,
        );
        console.log(`📋 Available ranges: ${sortedRanges.join(", ")}`);
      }

      return matchingAmount;
    };

    calculateScholarship();
  }, [grade, examType, totalMarks, marksObtained, rulesData]);

  const bannerImage = isMobile ? scholarshipBannerMobile : scholarshipBannerWeb;

  // Loading state
  if (loading) {
    return (
      <div className="scholarship-banner scholarship-loading">
        Loading scholarship information...
      </div>
    );
  }

  // Error state
  if (error) {
    return <div className="scholarship-banner scholarship-error">{error}</div>;
  }

  // Don't render if no scholarship or no rules
  if (!scholarshipAmount || !rulesData) {
    return null;
  }

  // Render the banner
  return (
    <div className="banner-container">
      <img
        src={bannerImage}
        alt={`Scholarship of ${scholarshipAmount}%`}
        className="banner-image"
        title={`You won a scholarship of ${scholarshipAmount}%`}
      />
      <h1 className="scholarship-banner_amount">{scholarshipAmount}%</h1>
    </div>
  );
};

export default AtreyaScholarshipBanner;
